import {
  useAdminCouponDetailsQuery,
  useRemoveCouponCode,
  useUpdateCouponCode,
} from '@streamster/shared/api/api.admin';
import mixins from '@streamster/shared/app/styles';
import Button from '@streamster/shared/components/Button/Button';
import Heading from '#/components/Heading/Heading';
import Loader from '@streamster/shared/components/Loader/Loader';
import PageWrapper from '#/components/PageWrapper/PageWrapper';
import { useAppNavigation, useAppRoute } from '@streamster/shared/navigation/navigation.ref';
import CouponForm from '#/screens/adminCouponCodes/CouponForm/CouponForm';
import { useToasterContext } from '@streamster/shared/toaster/toaster.provider';
import React from 'react';
import { View } from 'react-native';

export const EditCoupon: React.FC = () => {
  const { params } = useAppRoute<'AdminCouponCodes'>();
  const navigation = useAppNavigation();
  const id = params?.id;
  const toaster = useToasterContext();

  const { data, isLoading } = useAdminCouponDetailsQuery(id, { enabled: Boolean(id) });
  const { data: usages, isLoading: usagesLoading } = { data: [], isLoading: false };

  const { mutateAsync: updateCoupon } = useUpdateCouponCode();
  const { mutateAsync: removeCoupon } = useRemoveCouponCode();

  if (isLoading) return <Loader size="large" />;

  const handleSubmit = async (payload: any) => {
    if (!id) return;
    await updateCoupon({ code: id, payload });
    toaster.success({ title: 'Coupon updated', message: `Coupon ${id} updated` });
    navigation.navigate('AdminCouponCodes', { view: 'list' });
  };

  const handleDelete = async () => {
    if (!id) return;
    // confirm
    // eslint-disable-next-line no-restricted-globals
    if (!confirm(`Remove coupon? This action cannot be undone.`)) return;

    await removeCoupon({ id });
    toaster.success({ title: 'Coupon removed', message: `Coupon ${data?.code} removed` });
    navigation.navigate('AdminCouponCodes', { view: 'list' });
  };

  return (
    <PageWrapper>
      <View style={{ display: 'flex', gap: 10 }}>
        <View
          style={[
            mixins().styles.flexRow,
            mixins().styles.justifySpaceBetween,
            mixins().styles.alignItemsCenter,
          ]}
        >
          <Heading variant="page">Edit Coupon</Heading>
          <Button
            onPress={() => navigation.navigate('AdminCouponCodes', { view: 'list' })}
            secondary
          >
            Back to list
          </Button>
        </View>

        {!data && <div>No data available for this coupon</div>}

        {data && (
          <div style={{ marginTop: 10 }}>
            <CouponForm
              initial={{
                code: data.code,
                type: data.type,
                tariffId: data.tariffId ?? null,
                rules: data.rules ?? undefined,
                usagesLeft: data.usagesLeft ?? 1,
              }}
              onSubmit={handleSubmit}
              submitLabel="Save Changes"
              onCancel={() => navigation.navigate('AdminCouponCodes', { view: 'list' })}
              onDelete={handleDelete}
            />

            <Heading style={{ marginTop: 24 }}>Usages</Heading>

            {usagesLoading && <Loader />}

            {usages && usages.length === 0 && <div>No usages yet</div>}
          </div>
        )}
      </View>
    </PageWrapper>
  );
};

export default EditCoupon;
